import { Redirect, RouteComponentProps, Switch } from "react-router-dom";
import PreviewArea from "src/Pages/Company/OrganizationStructure/Areas/PreviewArea";
import React, { useEffect } from "react";
import StructureSettings from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureSettings";
import StructureAreas from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureAreas/StructureAreas";
import StructureView from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView";
import useFetchListOfStructures from "src/Pages/Company/OrganizationStructure/api/useFetchListOfStructures";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { Loader } from "src/Components/Loader/Loader";
import { useStructureStore } from "src/Pages/Company/OrganizationStructure/context/useStructureStore";
import HeaderInfo from "src/Pages/Company/OrganizationStructure/sharedComponents/HeaderInfo";
import PreviewStructures from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs";
import CreateArea from "src/Pages/Company/OrganizationStructure/Areas/CreateArea";
import SecureRoute from "src/utils/RoleBasedSecurity/SecurityComponents/SecureRoute";
import {
  organizationStructureModuleRead,
  organizationStructureAreaAdd,
} from "src/utils/RoleBasedSecurity/permissions";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import __ from "src/utils/Translations";
import { useCompanyBreadcrumbs } from "src/Pages/Company/breadcrumbsHelper";

export default function OrganizationStructure({ match }: RouteComponentProps) {
  const companyId = useCompanyId();
  const { data, isLoading } = useFetchListOfStructures(companyId);
  const structureId = data?.[0]?.structureId;
  const saveStructureId = useStructureStore((state) => state.setStructureId);

  useEffect(() => {
    if (structureId) {
      saveStructureId(structureId);
    }
  }, [structureId, saveStructureId]);

  const breadcrumbs = useCompanyBreadcrumbs([
    {
      label: __("Struktura organizacji"),
      path: "/organization-structure",
    },
    {
      label: __("Konfiguracja struktury"),
      path: "/organization-structure/configure-structure",
    },
  ]);

  return (
    <>
      <BreadcrumbsMui items={breadcrumbs} sx={{ marginBottom: "32px" }} />
      <Switch>
        <SecureRoute
          permission={organizationStructureModuleRead}
          exact
          path={`${match.url}`}
          render={() => {
            if (isLoading || !companyId) return <Loader />;
            if (!structureId)
              return <Redirect to={`${match.url}/create-structure`} />;
            return <Redirect to={`${match.url}/structure-areas`} />;
          }}
        />
        <SecureRoute
          permission={organizationStructureModuleRead}
          path={[
            `${match.url}/structure-areas`,
            `${match.url}/structure-view`,
            `${match.url}/structure-settings`,
          ]}
          render={(routeProps: RouteComponentProps) => {
            const { location } = routeProps;

            if (isLoading || !companyId) {
              return <Loader />;
            }

            return (
              <>
                <HeaderInfo companyId={companyId} structureId={structureId!} />
                <PreviewStructures />
                <Switch location={location}>
                  <SecureRoute
                    permission={organizationStructureModuleRead}
                    exact
                    path={`${match.url}/structure-areas`}
                    render={(routeProps: RouteComponentProps) => (
                      <StructureAreas
                        {...routeProps}
                        structureId={structureId!}
                      />
                    )}
                  />
                  <SecureRoute
                    permission={organizationStructureModuleRead}
                    exact
                    path={`${match.url}/structure-view/:orgchartType`}
                    render={(routeProps: RouteComponentProps) => (
                      <StructureView
                        {...routeProps}
                        structureId={structureId!}
                        companyId={companyId}
                      />
                    )}
                  />
                  <SecureRoute
                    permission={organizationStructureModuleRead}
                    exact
                    path={`${match.url}/structure-view/:orgchartType/node/:nodeId`}
                    render={(routeProps: RouteComponentProps) => (
                      <StructureView
                        {...routeProps}
                        structureId={structureId!}
                        companyId={companyId}
                      />
                    )}
                  />
                  <SecureRoute
                    permission={organizationStructureModuleRead}
                    exact
                    path={`${match.url}/structure-settings`}
                    render={(routeProps: RouteComponentProps) => (
                      <StructureSettings
                        {...routeProps}
                        structureId={structureId!}
                      />
                    )}
                  />
                </Switch>
              </>
            );
          }}
        />
        <SecureRoute
          permission={organizationStructureModuleRead}
          exact
          path={`${match.url}/area/:areaId`}
          component={PreviewArea}
        />
        <SecureRoute
          permission={organizationStructureAreaAdd}
          exact
          path={`${match.url}/create-area`}
          component={CreateArea}
        />
      </Switch>
    </>
  );
}
